<script setup>
import { useCampaignAvailability } from 'dashboard/composables/useCampaignAvailability';
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DeliveryErrorsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/DeliveryErrorsDialog.vue';
import CampaignProgress from 'dashboard/components-next/Campaigns/CampaignCard/CampaignProgress.vue';
import CampaignFunnel from 'dashboard/components-next/Campaigns/Analytics/CampaignFunnel.vue';
import CampaignMetadataPanel from 'dashboard/components-next/Campaigns/Analytics/CampaignMetadataPanel.vue';
import CampaignCostCard from 'dashboard/components-next/Campaigns/Analytics/CampaignCostCard.vue';
import CampaignTimelineChart from 'dashboard/components-next/Campaigns/Analytics/CampaignTimelineChart.vue';
import CampaignSmartTable from 'dashboard/components-next/Campaigns/Analytics/CampaignSmartTable.vue';
import RetargetingDialog from 'dashboard/components-next/Campaigns/Analytics/RetargetingDialog.vue';

const props = defineProps({
  campaignId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const isLoading = ref(true);
const campaign = ref(null);
const analytics = ref(null);
const showMetadata = ref(true);

const allCampaigns = useMapGetter('campaigns/getAllCampaigns');

const fetchAnalytics = async () => {
  try {
    const { data } = await CampaignsAPI.getAnalytics(props.campaignId);
    analytics.value = data;
  } catch {
    analytics.value = null;
  }
};

const fetchCampaignFromStore = () => {
  const records = allCampaigns.value || [];
  return records.find(c => String(c.id) === String(props.campaignId));
};

const loadCampaignData = async () => {
  isLoading.value = true;
  analytics.value = null;
  campaign.value = fetchCampaignFromStore();

  if (!campaign.value) {
    await store.dispatch('campaigns/get');
    campaign.value = fetchCampaignFromStore();
  }

  await fetchAnalytics();
  isLoading.value = false;
};

// Reload data when campaignId changes (handles keep-alive reuse)
watch(() => props.campaignId, loadCampaignData, { immediate: true });

const deliveryErrorsDialogRef = ref(null);
const openDeliveryErrors = () =>
  deliveryErrorsDialogRef.value?.dialogRef.open();

const retargetingDialogRef = ref(null);
const retargetData = ref({ actionType: 'new_campaign', contactIds: [] });

const handleRetarget = async data => {
  retargetData.value = data;
  await nextTick();
  retargetingDialogRef.value?.dialogRef?.open();
};

const goBack = () => {
  router.push({ name: 'campaigns_whatsapp_index' });
};

const exportCsv = async () => {
  try {
    const { data } = await CampaignsAPI.exportContacts(props.campaignId);
    downloadCsvFile(
      `campaign_${campaign.value?.display_id || props.campaignId}_contacts.csv`,
      data
    );
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.ANALYTICS.EXPORT_ERROR'));
  }
};

const hasImportErrors = computed(
  () => campaign.value?.campaign_import?.failed_rows > 0
);

const downloadImportErrors = async () => {
  try {
    const { data } = await CampaignsAPI.downloadImportErrors(props.campaignId);
    downloadCsvFile(
      `campaign_${campaign.value?.display_id || props.campaignId}_import_errors.csv`,
      data
    );
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.ANALYTICS.DOWNLOAD_ERROR'));
  }
};

const campaignDuration = computed(() => {
  if (!campaign.value?.started_at || !campaign.value?.completed_at) return null;
  const start = new Date(campaign.value.started_at * 1000);
  const end = new Date(campaign.value.completed_at * 1000);
  const diffMs = end - start;
  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
});

const isProcessing = computed(
  () => campaign.value?.campaign_status === 'processing'
);
const isPaused = computed(() => campaign.value?.campaign_status === 'paused');
const isActive = computed(() => isProcessing.value || isPaused.value);

const statusBadge = computed(() => {
  const status = campaign.value?.campaign_status;
  const badges = {
    processing: {
      label: t('CAMPAIGN.LIST.STATUS.PROCESSING'),
      class: 'bg-n-brand/10 text-n-brand',
    },
    paused: {
      label: t('CAMPAIGN.LIST.STATUS.PAUSED'),
      class: 'bg-n-amber-3 text-n-amber-11',
    },
    completed: {
      label: t('CAMPAIGN.LIST.STATUS.COMPLETED'),
      class: 'bg-n-teal-3 text-n-teal-11',
    },
    stopped: {
      label: t('CAMPAIGN.LIST.STATUS.STOPPED'),
      class: 'bg-n-slate-3 text-n-slate-11',
    },
  };
  return badges[status] || null;
});

const handlePause = async () => {
  try {
    await CampaignsAPI.pause(campaign.value.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE_SUCCESS'));
    await store.dispatch('campaigns/get');
    campaign.value = fetchCampaignFromStore();
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE_ERROR'));
  }
};

const handleResume = async () => {
  try {
    await CampaignsAPI.resume(campaign.value.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME_SUCCESS'));
    await store.dispatch('campaigns/get');
    campaign.value = fetchCampaignFromStore();
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME_ERROR'));
  }
};

const confirmStopDialogRef = ref(null);

const handleStop = () => {
  confirmStopDialogRef.value?.open();
};

const confirmStop = async () => {
  try {
    await CampaignsAPI.stop(campaign.value.id);
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_SUCCESS'));
    await store.dispatch('campaigns/get');
    campaign.value = fetchCampaignFromStore();
  } catch {
    useAlert(t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_ERROR'));
  } finally {
    confirmStopDialogRef.value?.close();
  }
};

// Keep campaign ref in sync with store (updated via ActionCable)
const storeCampaign = computed(() => fetchCampaignFromStore());

watch(
  () => [
    storeCampaign.value?.processed_contacts,
    storeCampaign.value?.campaign_status,
  ],
  () => {
    if (storeCampaign.value) campaign.value = storeCampaign.value;
  }
);
const { campaignsEnabled } = useCampaignAvailability();
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-8 hover:bg-n-alpha-2 transition-colors cursor-pointer"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4 text-n-slate-11" />
        </button>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ campaign?.title || t('CAMPAIGN.WHATSAPP.ANALYTICS.TITLE') }}
        </h2>
        <span
          v-if="campaignDuration"
          class="text-xs text-n-slate-10 px-2 py-0.5 rounded bg-n-alpha-2"
        >
          {{ campaignDuration }}
        </span>
        <span
          v-if="statusBadge"
          class="text-xs font-medium px-2 py-0.5 rounded"
          :class="statusBadge.class"
        >
          {{ statusBadge.label }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="campaign"
          :label="t('CAMPAIGN.WHATSAPP.DELIVERY_ERRORS.TITLE')"
          variant="faded"
          icon="i-lucide-alert-triangle"
          size="sm"
          @click="openDeliveryErrors"
        />
        <Button
          v-if="isProcessing"
          :label="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE')"
          variant="faded"
          icon="i-lucide-pause"
          size="sm"
          color="amber"
          @click="handlePause"
        />
        <Button
          v-if="isPaused"
          :label="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME')"
          :disabled="!campaignsEnabled"
          variant="faded"
          icon="i-lucide-play"
          size="sm"
          color="blue"
          @click="handleResume"
        />
        <Button
          v-if="isActive"
          :label="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP')"
          variant="faded"
          icon="i-lucide-circle-stop"
          size="sm"
          color="slate"
          @click="handleStop"
        />
        <Button
          v-if="hasImportErrors"
          label="Download errors"
          variant="faded"
          icon="i-lucide-alert-triangle"
          size="sm"
          color="ruby"
          @click="downloadImportErrors"
        />
        <Button
          :label="t('CAMPAIGN.WHATSAPP.ANALYTICS.EXPORT_CSV')"
          variant="faded"
          icon="i-lucide-download"
          size="sm"
          @click="exportCsv"
        />
      </div>
    </div>

    <!-- Progress bar during processing/paused -->
    <div v-if="isActive && campaign" class="px-6 py-3 border-b border-n-weak">
      <CampaignProgress
        :total-contacts="campaign.total_contacts || 0"
        :processed-contacts="campaign.processed_contacts || 0"
        :failed-contacts="campaign.failed_contacts || 0"
        :is-processing="isProcessing"
        @view-errors="openDeliveryErrors"
      />
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <div v-else class="flex flex-1 overflow-hidden">
      <!-- Main content -->
      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
        <!-- Funnel -->
        <CampaignFunnel v-if="analytics" :summary="analytics.summary" />

        <!-- Cost + Timeline row -->
        <div class="grid grid-cols-3 gap-4">
          <CampaignCostCard
            v-if="analytics"
            :cost-breakdown="analytics.cost_breakdown"
          />
          <div class="col-span-2">
            <CampaignTimelineChart
              v-if="analytics"
              :timeline="analytics.timeline"
            />
          </div>
        </div>

        <!-- Smart Table -->
        <CampaignSmartTable
          :campaign-id="campaignId"
          @retarget="handleRetarget"
        />
      </div>

      <!-- Metadata sidebar -->
      <div
        class="relative border-l border-n-weak transition-all duration-300"
        :class="showMetadata ? 'w-80' : 'w-10'"
      >
        <button
          type="button"
          class="absolute -left-3 top-4 z-10 flex items-center justify-center rounded-full size-6 bg-n-solid-2 border border-n-weak hover:bg-n-alpha-3 transition-colors cursor-pointer"
          @click="showMetadata = !showMetadata"
        >
          <span
            class="size-3.5 text-n-slate-11 transition-transform"
            :class="
              showMetadata ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'
            "
          />
        </button>
        <CampaignMetadataPanel v-if="showMetadata" :campaign="campaign" />
      </div>
    </div>

    <DeliveryErrorsDialog ref="deliveryErrorsDialogRef" :campaign="campaign" />

    <RetargetingDialog
      v-if="campaignsEnabled || retargetData.actionType !== 'new_campaign'"
      ref="retargetingDialogRef"
      :campaign-id="campaignId"
      :action-type="retargetData.actionType"
      :contact-ids="retargetData.contactIds"
    />

    <Dialog
      ref="confirmStopDialogRef"
      type="alert"
      :title="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.TITLE')"
      :description="
        t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.DESCRIPTION', {
          campaignName: campaign?.title || '',
        })
      "
      :confirm-button-label="
        t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.STOP_CONFIRM.CONFIRM')
      "
      @confirm="confirmStop"
    />
  </div>
</template>
